import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { inject } from 'mobx-react';
import FeedbackProblemPicker, {
    parseProblemCategories,
    type FeedbackProblemCategory,
    type FeedbackProblemItem,
} from '@components/feedbackProblemPicker';
import FeedbackProductPicker, {
    parseProductCategories,
    fetchProductOptions,
    type FeedbackProductCategory,
    type FeedbackProductItem,
    type FetchProductsParams,
} from '@components/feedbackProductPicker';
import type { ThreadAction } from '@stores/thread/action';

export interface FeedbackProblemProductPickersHandle {
    openProblemPicker: () => void;
    openProductPicker: () => void;
}

export interface FeedbackProblemProductPickersProps {
    thread?: ThreadAction;
    problemId?: string;
    productId?: string;
    problemCategories?: FeedbackProblemCategory[];
    productCategories?: FeedbackProductCategory[];
    fetchProducts?: (params: FetchProductsParams) => Promise<FeedbackProductItem[]>;
    onProblemSelect?: (problem: FeedbackProblemItem | null) => void;
    onProductSelect?: (product: FeedbackProductItem | null) => void;
}

/**
 * 问题类型 + 手机型号选择器组合（与反馈列表筛选共用）
 * 封装弹框展示、分类数据加载与机型列表请求
 */
const FeedbackProblemProductPickers = forwardRef<
    FeedbackProblemProductPickersHandle,
    FeedbackProblemProductPickersProps
>(function FeedbackProblemProductPickers(props, ref) {
    const {
        thread,
        problemId,
        productId,
        problemCategories: problemCategoriesProp,
        productCategories: productCategoriesProp,
        fetchProducts: fetchProductsProp,
        onProblemSelect,
        onProductSelect,
    } = props;

    const [problemCategories, setProblemCategories] = useState<FeedbackProblemCategory[]>(problemCategoriesProp || []);
    const [productCategories, setProductCategories] = useState<FeedbackProductCategory[]>(productCategoriesProp || []);
    const [problemPickerVisible, setProblemPickerVisible] = useState(false);
    const [productPickerVisible, setProductPickerVisible] = useState(false);

    const onProblemSelectRef = useRef(onProblemSelect);
    const onProductSelectRef = useRef(onProductSelect);

    useEffect(() => {
        onProblemSelectRef.current = onProblemSelect;
    }, [onProblemSelect]);

    useEffect(() => {
        onProductSelectRef.current = onProductSelect;
    }, [onProductSelect]);

    useImperativeHandle(ref, () => ({
        openProblemPicker: () => setProblemPickerVisible(true),
        openProductPicker: () => setProductPickerVisible(true),
    }));

    useEffect(() => {
        if (problemCategoriesProp) {
            setProblemCategories(problemCategoriesProp);
        }
    }, [problemCategoriesProp]);

    useEffect(() => {
        if (productCategoriesProp) {
            setProductCategories(productCategoriesProp);
        }
    }, [productCategoriesProp]);

    useEffect(() => {
        if (problemCategoriesProp?.length && productCategoriesProp?.length) {
            return;
        }
        if (!thread) return;

        let cancelled = false;

        const loadOptions = async () => {
            try {
                const [problemsRes, productsRes] = await Promise.all([
                    thread.getFeedbackProblems(),
                    thread.getFeedbackProducts(),
                ]);
                if (cancelled) return;
                if (!problemCategoriesProp?.length) {
                    setProblemCategories(parseProblemCategories(problemsRes));
                }
                if (!productCategoriesProp?.length) {
                    setProductCategories(parseProductCategories(productsRes));
                }
            } catch (error) {
                console.error('[FeedbackProblemProductPickers] load options failed:', error);
            }
        };

        loadOptions();

        return () => {
            cancelled = true;
        };
    }, [thread, problemCategoriesProp, productCategoriesProp]);

    const fetchProducts = useCallback(async (params: FetchProductsParams) => {
        if (fetchProductsProp) {
            return fetchProductsProp(params);
        }
        if (!thread) return [];
        return fetchProductOptions(params => thread.getProducts(params), params);
    }, [thread, fetchProductsProp]);

    return (
        <>
            <FeedbackProblemPicker
                visible={problemPickerVisible}
                categories={problemCategories}
                value={problemId}
                onClose={() => setProblemPickerVisible(false)}
                onConfirm={(problem) => {
                    setProblemPickerVisible(false);
                    onProblemSelectRef.current?.(problem);
                }}
            />
            <FeedbackProductPicker
                visible={productPickerVisible}
                categories={productCategories}
                value={productId}
                fetchProducts={fetchProducts}
                onClose={() => setProductPickerVisible(false)}
                onConfirm={(product) => {
                    setProductPickerVisible(false);
                    onProductSelectRef.current?.(product);
                }}
            />
        </>
    );
});

export default inject('thread')(FeedbackProblemProductPickers);
